import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { AGGClient } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import { Button } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { experiments, useExperiment } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { getIsSMBSalesLead } from "./contact-sales-cta-helpers";

const fullWidthStyles = css({
    maxWidth: "none",
    width: "100%"
});

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const { accountId } = useLoggedInUser();
    // feature gate has configuration to only be enabled on specified page paths
    // https://switcheroo.atlassian.com/ui/gates/c5b6c155-5937-4f7d-9a5f-154015312a23/key/wac_sales_leads_cta_global_nav
    const isFeatureGateEnabled = checkFeatureFlagGate(
        featureFlags.wacSalesLeadsCtaGlobalNav
    );
    const jiraExperiment = useExperiment(
        experiments.gwp10447WacSalesLeadsJiraPT
    );
    const confluenceExperiment = useExperiment(
        experiments.gwp10447WacSalesLeadsConfluencePT
    );

    const jiraExperimentEnrolled = jiraExperiment.type === "enrolled";
    const confluenceExperimentEnrolled =
        confluenceExperiment.type === "enrolled";

    const [isSalesLead, setIsSalesLead] = useState<boolean>(false);

    useEffect(() => {
        if (!isFeatureGateEnabled || !accountId) return;
        const aggClient = new AGGClient({
            gatewayUrl:
                process.env["NODE_ENV"] === "production"
                    ? "https://www.atlassian.com/gateway/api/graphql"
                    : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
        });

        const isSMBSalesLead = async () => {
            getIsSMBSalesLead({
                accountId,
                aggClient
            })
                .then((response) => {
                    setIsSalesLead(!!response);
                })
                .catch(() => {
                    console.error(
                        "Error fetching GUPS user profile for SMB Sales lead"
                    );
                });
        };
        isSMBSalesLead();
    }, [accountId, isFeatureGateEnabled]);

    if (!accountId || !isFeatureGateEnabled || !isSalesLead) {
        return;
    }

    // Fire exposures for treatment and control groups
    if (jiraExperimentEnrolled) {
        jiraExperiment.fireExposure();
    }
    if (confluenceExperimentEnrolled) {
        confluenceExperiment.fireExposure();
    }

    // Experiment enrollment implies the feature gate is enabled on that path.
    // If enrolled but in control group (value is false), don't render.
    if (jiraExperimentEnrolled && !jiraExperiment.value) {
        return;
    }
    if (confluenceExperimentEnrolled && !confluenceExperiment.value) {
        return;
    }

    // Homepage: no experiment enrolled, just feature gate
    // Jira/Confluence: experiment enrolled with truthy value
    return <CtaButtonRenderer isFullWidth={isFullWidth} />;
};

const CtaButtonRenderer = ({ isFullWidth }: { isFullWidth?: boolean }) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const localizedHref = useLocalizedHref(
        "/enterprise/contact?formType=product-features"
    );

    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (analyticsClient && !hasFiredRef.current) {
            hasFiredRef.current = true;
            createAnalyticsEvent({
                eventType: "ui",
                action: "viewed",
                actionSubject: "button",
                actionSubjectId: "contactSalesCta",
                source: "globalNav",
                attributes: {
                    isMobile: !!isFullWidth
                }
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent, isFullWidth]);

    return (
        <Button
            variant="primary_alt"
            css={isFullWidth && fullWidthStyles}
            href={localizedHref}
        >
            <FormattedMessage
                id="global-nav.contact-sales-cta.label"
                defaultMessage="Contact us"
            />
        </Button>
    );
};
